import { useEffect, useState } from 'react';
import { ArrowLeft, ArrowRight, Minus, Plus, ShoppingCart, Trash2 } from 'lucide-react';
import { BrandLogo } from './BrandLogo';
import { LegalFooter } from './LegalFooter';
import { clearCartId, loadCartId } from '../lib/cartStorage';
import { formatApiPrice, getCart, removeCartItem, updateCartItem, type CartPayload } from '../lib/commerceApi';
import { trackEvent } from '../lib/analytics';

export function CartPage() {
  const [cartId, setCartId] = useState<string | null>(() => loadCartId());
  const [cart, setCart] = useState<CartPayload | null>(null);
  const [message, setMessage] = useState('Winkelwagen laden...');
  const [isBusy, setIsBusy] = useState(false);

  useEffect(() => {
    document.title = 'Winkelwagen | ProPickleball.nl';
  }, []);

  useEffect(() => {
    if (!cartId) {
      setCart(null);
      setMessage('Je winkelwagen is nog leeg.');
      return;
    }

    let isActive = true;
    getCart(cartId)
      .then((payload) => {
        if (!isActive) return;
        setCart(payload);
        setMessage(payload.items.length ? 'Controleer je selectie voordat je verdergaat.' : 'Je winkelwagen is nog leeg.');
      })
      .catch(() => {
        if (!isActive) return;
        clearCartId();
        setCartId(null);
        setMessage('Je winkelwagen kon niet worden opgehaald. Start opnieuw vanuit een productpagina.');
      });

    return () => {
      isActive = false;
    };
  }, [cartId]);

  async function refreshCart() {
    if (!cartId) return;
    const payload = await getCart(cartId);
    setCart(payload);
  }

  async function changeQuantity(sku: string, nextQuantity: number) {
    if (!cartId || nextQuantity < 1) return;
    setIsBusy(true);
    try {
      await updateCartItem(cartId, sku, nextQuantity);
      await refreshCart();
      setMessage('Aantal bijgewerkt.');
    } catch {
      setMessage('Dit aantal is nu niet beschikbaar voor online bestellen.');
      trackEvent({ event: 'checkout_blocked', location: 'cart', label: 'quantity_update_blocked', sku, routeFamily: 'checkout' });
    } finally {
      setIsBusy(false);
    }
  }

  async function removeItem(sku: string) {
    if (!cartId) return;
    setIsBusy(true);
    try {
      await removeCartItem(cartId, sku);
      await refreshCart();
      setMessage('Product verwijderd uit je winkelwagen.');
    } catch {
      setMessage('Verwijderen lukte niet. Probeer het opnieuw.');
    } finally {
      setIsBusy(false);
    }
  }

  const hasItems = Boolean(cart?.items.length);
  const canCheckout = Boolean(cart?.items.length && cart.items.every((item) => item.checkoutEligible));

  return (
    <>
      <main className="cartPage">
        <nav className="productTopbar" aria-label="Winkelwagen navigatie">
          <BrandLogo compact />
          <a className="backHome" href="/producten/"><ArrowLeft size={18} /> Verder winkelen</a>
        </nav>

        <section className="cartShell">
          <div className="cartHeading">
            <p className="eyebrow">Winkelwagen</p>
            <h1>Controleer je selectie.</h1>
            <p>
              Online bestellen is alleen beschikbaar voor producten die server-side zijn vrijgegeven met prijs, voorraad, betaling en servicevoorwaarden.
            </p>
          </div>

          <div className="cartGrid">
            <section className="cartItemsPanel" aria-label="Winkelwagen producten">
              {!hasItems ? (
                <article className="emptyResults">
                  <ShoppingCart />
                  <h2>Je winkelwagen is leeg.</h2>
                  <p>{message}</p>
                  <a className="primaryCta" href="/producten/">Bekijk producten</a>
                </article>
              ) : (
                cart?.items.map((item) => (
                  <article className="cartLineItem" key={item.sku}>
                    <div>
                      <span>SKU {item.sku}</span>
                      <h2>{item.name}</h2>
                      <p>{item.checkoutEligible ? 'Beschikbaar voor pilot-checkout.' : 'Niet beschikbaar voor online checkout.'}</p>
                    </div>
                    <div className="cartQuantityControl" aria-label={`Aantal voor ${item.name}`}>
                      <button type="button" disabled={isBusy || item.quantity <= 1} onClick={() => changeQuantity(item.sku, item.quantity - 1)}><Minus size={16} /></button>
                      <strong>{item.quantity}</strong>
                      <button type="button" disabled={isBusy} onClick={() => changeQuantity(item.sku, item.quantity + 1)}><Plus size={16} /></button>
                    </div>
                    <strong>{formatApiPrice(item.lineTotalCents)}</strong>
                    <button className="iconButton" type="button" disabled={isBusy} onClick={() => removeItem(item.sku)} aria-label={`${item.name} verwijderen`}>
                      <Trash2 size={17} />
                    </button>
                  </article>
                ))
              )}
            </section>

            <aside className="checkoutSummaryPanel" aria-label="Samenvatting">
              <h2>Samenvatting</h2>
              <dl>
                <div><dt>Subtotaal</dt><dd>{formatApiPrice(cart?.totalCents ?? 0)}</dd></div>
                <div><dt>Verzending</dt><dd>Wordt berekend in checkout</dd></div>
                <div><dt>Status</dt><dd>{canCheckout ? 'Checkout beschikbaar' : 'Checkout nog niet beschikbaar'}</dd></div>
              </dl>
              <p role="status">{message}</p>
              {canCheckout ? (
                <a className="primaryCta fullWidthCta" href="/checkout/" onClick={() => trackEvent({ event: 'begin_checkout', location: 'cart', label: 'checkout_cta', routeFamily: 'checkout' })}>
                  Naar checkout <ArrowRight size={18} />
                </a>
              ) : (
                <a className="secondaryDarkCta fullWidthCta" href="/#nieuwsbrief">
                  Ontvang update bij beschikbaarheid
                </a>
              )}
            </aside>
          </div>
        </section>
      </main>
      <LegalFooter />
    </>
  );
}
